import { Color } from 'three';
import { ViewerError, ViewerErrorCode } from '../../../Utils';
import { Model3D } from './Model3D';
/**
 * IFC model.
 */
export class IFC extends Model3D {
    /**
     * Create a new IFC model.
     *
     * @param object
     * @param toggleListeners
     */
    constructor(object) {
        super(object);
        Object.defineProperty(this, "ifc", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "elementTree", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.ifc = object;
        this.elementTree = null;
    }
    /**
     * Build the element tree.
     */
    buildElementTree() {
        let root = null;
        const queue = [[this.ifc.getRootElementId(), null]];
        while (queue.length > 0) {
            const current = queue.pop();
            if (!current)
                continue;
            const [id, parent] = current;
            const element = this.ifc.getElementProperties(id);
            if (!element)
                continue;
            const childrenIds = element.children;
            const node = element;
            node.children = [];
            if (parent) {
                parent.children.push(node);
            }
            else {
                root = node;
            }
            for (const child of childrenIds) {
                queue.push([child, node]);
            }
        }
        if (root === null) {
            throw new ViewerError(ViewerErrorCode.MissingIFCRoot, 'Missing IFC root.');
        }
        return root;
    }
    /**
     * Generate the IFC element tree.
     *
     * @returns
     */
    getElementTree() {
        if (this.elementTree === null) {
            this.elementTree = this.buildElementTree();
        }
        return this.elementTree;
    }
    /**
     * Get the ids of all the physical descendants of an element.
     *
     * @param elementId
     * @param exclude
     * @returns
     */
    getPhysicalDescendants(elementId, exclude) {
        const root = this.getElementTree();
        const elementIds = [];
        const queue = [[root, root.id === elementId]];
        const excludeSet = new Set(exclude);
        while (queue.length > 0) {
            const current = queue.pop();
            if (!current)
                continue;
            const [node, parentDescendant] = current;
            const isDescendant = parentDescendant || node.id === elementId;
            if (isDescendant && node.isPhysicalMesh && !excludeSet.has(node.id)) {
                elementIds.push(node.id);
            }
            for (const child of node.children) {
                queue.push([child, isDescendant]);
            }
        }
        return elementIds;
    }
    /**
     * Set the style of the IFC by hiding and highlighting groups of elements.
     *
     * @param hiddenElementIds
     * @param selectedElementIds
     */
    setStyle(hiddenElementIds, selectedElementIds) {
        this.ifc.resetMaterials();
        this.ifc.hideGroup(hiddenElementIds);
        this.ifc.applyGroupMaterial(selectedElementIds, {
            type: 'basicAlpha',
            color: new Color(1, 0, 1),
            alpha: 0.5,
        });
    }
}
